import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CalendarDays, Search, Users } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import { DestinationCard, PackageCard, VisaCard } from "@/components/Cards";
import { searchContent, useContent } from "@/lib/content";
import { useEnquiry } from "@/components/EnquiryModal";

const fmt = (s) => { try { return format(parseISO(s), "dd MMM yyyy"); } catch { return s; } };

export default function SearchResults() {
  const [params] = useSearchParams();
  const { destinations, packages, visas, loading } = useContent();
  const { openEnquiry } = useEnquiry();
  const [q, setQ] = useState(params.get("destination") || "");

  const query = q || params.get("destination") || "";
  const results = useMemo(
    () => searchContent({ destinations, packages, visas }, query),
    [destinations, packages, visas, query]
  );

  const from = params.get("from");
  const to = params.get("to");
  const adults = params.get("adults");
  const children = params.get("children");
  const empty = !loading && results.packages.length === 0 && results.destinations.length === 0 && results.visas.length === 0;

  return (
    <div data-testid="search-results-page" className="py-14 lg:py-20">
      <SEO title={query ? `Search: ${query}` : "Search"} description="Search D Travel Link destinations, holiday packages and visa guides." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-skybrand">Search Results</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
          {query ? `Results for "${query}"` : "Search D Travel Link"}
        </h1>
        <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-500">
          {from && (
            <span className="flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-100 px-3.5 py-1.5 font-semibold text-royal">
              <CalendarDays className="w-4 h-4" /> {fmt(from)}{to ? ` → ${fmt(to)}` : ""}
            </span>
          )}
          {adults && (
            <span className="flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-100 px-3.5 py-1.5 font-semibold text-royal">
              <Users className="w-4 h-4" /> {adults} Adult{adults !== "1" ? "s" : ""}{children ? `, ${children} Child` : ""}
            </span>
          )}
        </div>

        <div className="mt-6 flex items-center gap-2.5 h-12 max-w-xl rounded-xl border border-slate-200 bg-white px-4 focus-within:ring-2 focus-within:ring-skybrand">
          <Search className="w-4 h-4 text-skybrand shrink-0" />
          <input data-testid="search-page-input" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search destinations, packages, visas..."
                 className="w-full bg-transparent text-sm focus:outline-none" />
        </div>

        {loading ? (
          <p className="mt-10 text-slate-400">Searching...</p>
        ) : empty ? (
          <div className="mt-12 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="search-empty">
            <h2 className="font-heading text-xl font-bold text-slate-900">No exact matches{query ? ` for "${query}"` : ""}</h2>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              We plan fully customised trips to 100+ destinations — even ones not listed yet. Tell us where you want to go.
            </p>
            <button onClick={() => openEnquiry({ destination: query })} data-testid="search-empty-enquire"
                    className="mt-5 rounded-full bg-navy px-7 py-3 text-sm font-bold text-white hover:bg-royal transition-colors">
              Enquire About {query || "My Trip"}
            </button>
          </div>
        ) : (
          <>
            {results.destinations.length > 0 && (
              <Reveal className="mt-10">
                <h2 className="font-heading text-xl font-bold text-slate-900">Destinations</h2>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {results.destinations.map((d) => <DestinationCard key={d.slug} dest={d} />)}
                </div>
              </Reveal>
            )}
            {results.packages.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="font-heading text-xl font-bold text-slate-900">Packages</h2>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {results.packages.map((p) => <PackageCard key={p.slug} pkg={p} />)}
                </div>
              </Reveal>
            )}
            {results.visas.length > 0 && (
              <Reveal className="mt-12">
                <h2 className="font-heading text-xl font-bold text-slate-900">Visa Guides</h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {results.visas.map((v) => <VisaCard key={v.slug} visa={v} />)}
                </div>
              </Reveal>
            )}
          </>
        )}
      </div>
    </div>
  );
}
